import JSZip from "jszip";
import { MAPBOX_ACCESS_TOKEN } from "src/helper/constants";
import { dockConnectionManager } from "src/helper/HubConnectionManager";
import { convertToLngLat } from "src/helper/utils";
import { createWaypointTemplate, createWaypointWaylines } from "src/helper/waypointMarkupUtils";
import store from "src/redux/configureStore";
import { djiCloudCustomMethod, djiCloudMethod } from "src/services/common/constants";
import { getDeviceConnectionGroups, getDeviceCoordinates, getDeviceInfo } from "src/services/device/common/deviceUtils";
import { Waypoint } from "./useCreateMission";

export interface kmzWaypointData {
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number;
  actions: {};
}

export interface kmzInputData {
  droneEnumValue: number;
  droneSubEnumValue: number;
  payloadEnumValue: number;
  payloadSubEnumValue: number;
  speed: number;
  altitude: number;
  relativeAltitude: number;
  waypoints: Waypoint[];
}

export const createKmz = async (config: kmzInputData) => {

  const kmzConfig = {
    droneEnumValue: config.droneEnumValue ?? 91,
    droneSubEnumValue: config.droneSubEnumValue ?? 1,
    payloadEnumValue: config.payloadEnumValue ?? 53,
    payloadSubEnumValue: config.payloadSubEnumValue ?? 0,
    pyaloadPositionIndex: 0,
    autoFlightSpeed: Number(config.speed),
    globalHeight: Number(config.relativeAltitude),
    waypoints: config.waypoints?.map(item => ({
      lat: item.latitude,
      long: item.longitude,
    }))
  };

  const waypointTemplate = createWaypointTemplate(kmzConfig);
  const waypointWaylines = createWaypointWaylines(kmzConfig);

  const zip = new JSZip();
  const wpmz = zip.folder('wpmz');
  const resource = wpmz?.folder('res');
  let zipResult;

  wpmz?.file('template.kml', waypointTemplate);
  wpmz?.file('waylines.wpml', waypointWaylines);

  try {
    zipResult = await zip.generateAsync({ type: 'base64' });
    return zipResult;
  } catch (error) {
    console.error('Error creating KMZ file', error);
    return null;
  }
}

export const getMissionInfo = (queryFunction) => {
  const state = store.getState();
  const mission = state.missionService.missions.find(queryFunction);

  return mission;
}

export const getCoordinateGeocoding = (coordinate: any) => {
  const realCoordinate = convertToLngLat(coordinate);
  if(!realCoordinate) return null;

  return new Promise((resolve, reject) => {
    fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${realCoordinate[0]},${realCoordinate[1]}.json?access_token=${MAPBOX_ACCESS_TOKEN}`)
      .then(response => response.json())
      .then(data => {
        resolve(data);
      })
      .catch(error => {
        console.error('Error fetching address', error);
        reject(null);
      });
  });
}

export const terminateRemoteCloudMission = (deviceId) => {
  const deviceInfo = getDeviceInfo(deviceId);
  const groups = getDeviceConnectionGroups(deviceId);

  dockConnectionManager?.sendToDjiCloudGroup(deviceId, groups?.send?.commands, {
    method: djiCloudCustomMethod.mission.MISSION_TERMINATE,
    gateway: deviceInfo?.serialNumber,
    data: {},
  });
}

export const createMission = async (dockId, data) => {
  return new Promise((resolve, reject) => {
    const deviceInfo = getDeviceInfo(dockId);
    const connectionGroups = getDeviceConnectionGroups(dockId);
    const coordinates = getDeviceCoordinates(dockId);
    let timeoutId;

    const taskData = {
      task_type: 0,
      file: {
        url: "https://iruavaccmgtstgukprod.blob.core.windows.net/1fa6b1f9-c75a-4c72-9583-562e9ae8820f/drones/7CTDM4F00B760B/flights/14b5acd4-c202-4692-ae7b-6b117f794592.kmz?sv=2021-10-04&spr=https&st=2025-04-07T06%3A46%3A52Z&se=2026-04-02T06%3A47%3A22Z&sr=b&sp=r&rscd=attachment&sig=9yGjQ%2FDSRDs8KEI2CtnjisVmZYspdCXOWACuwOSmTsY%3D",
        fingerprint: "7bfbcb9b5a6598a9a7db99783b9ff6a0"
      },
      rth_altitude: 60,
      rth_mode: 1,
      out_of_control_action: 0,
      exit_wayline_when_rc_lost: 1,
      wayline_precision_type: 0,
      simulate_mission: {
        is_enable: 1,
        latitude: + parseFloat(coordinates?.lat).toFixed(7),
        longitude: + parseFloat(coordinates?.long).toFixed(7),
      },
      ...data
    };

    const {bid: requestBid} = dockConnectionManager.sendToDjiCloudGroup(dockId, connectionGroups?.send?.services, {
      method: djiCloudMethod.wayline.FLIGHTTASK_PREPARE,
      data: taskData,
    });

    dockConnectionManager.subscribeGroupMessages([
      {
        identity: dockId,
        name: [connectionGroups?.join?.services_reply],
        handler: (message) => {
          if(message?.method === djiCloudMethod.wayline.FLIGHTTASK_PREPARE && message?.bid === requestBid) {
            if(message.data?.result === 0) {
              console.log('💤⌛️✨ Waypoint mission created successfully');
              resolve(message);
            }
            else {
              console.log('💤⌛️❌ Waypoint mission creation failed', message);
              reject(message);
            }

            clearTimeout(timeoutId);
          }
        }
      },
    ], 'mission-issue');

    timeoutId = setTimeout(() => {
      reject(new Error('Keep-alive mission creation timed out'));
      console.log('💤⌛️❌ Keep-alive mission creation timed out');
    }, 5000);
  });
}

export const cancelMission = async (dockId, flightId) => {
  return new Promise((resolve, reject) => {
    const connectionGroups = getDeviceConnectionGroups(dockId);
    let timeoutId;

    const taskData = {
      flight_ids: [flightId],
    };

    const {bid: requestBid} = dockConnectionManager.sendToDjiCloudGroup(dockId, connectionGroups?.send?.services, {
      method: djiCloudMethod.wayline.FLIGHTTASK_UNDO,
      data: taskData,
    });

    dockConnectionManager.subscribeGroupMessages([
      {
        identity: dockId,
        name: [connectionGroups?.join?.services_reply],
        handler: (message) => {
          if(message?.method === djiCloudMethod.wayline.FLIGHTTASK_UNDO && message?.bid === requestBid) {
            if(message.data?.result === 0) {
              console.log('💤✋✨ Keep-alive mission cancellation succeed');
              resolve(message);
            }
            else {
              console.log('💤✋❌ Keep-alive mission cancellation failed', message);
              reject(message);
            }

            clearTimeout(timeoutId);
          }
        }
      },
    ], 'mission-issue');

    timeoutId = setTimeout(() => {
      reject(new Error('Keep-alive mission cancellation timed out'));
      console.log('💤✋❌ Keep-alive mission cancellation timed out');
    }, 5000);
  });
}

export const executeMission = async (dockId, flightId) => {
  return new Promise((resolve, reject) => {
    const connectionGroups = getDeviceConnectionGroups(dockId);
    let timeoutId;

    const taskData = {
      flight_id: flightId,
    };

    const {bid: requestBid} = dockConnectionManager.sendToDjiCloudGroup(dockId, connectionGroups?.send?.services, {
      method: djiCloudMethod.wayline.FLIGHTTASK_EXECUTE,
      data: taskData,
    });

    dockConnectionManager.subscribeGroupMessages([
      {
        identity: dockId,
        name: [connectionGroups?.join?.services_reply],
        handler: (message) => {
          if(message?.method === djiCloudMethod.wayline.FLIGHTTASK_EXECUTE && message?.bid === requestBid) {
            if(message.data?.result === 0) {
              console.log('💤✋✨ Keep-alive mission execute request succeed');
              resolve(message);
            }
            else {
              console.log('💤✋❌ Keep-alive mission execute request failed', message);
              reject(message);
            }

            clearTimeout(timeoutId);
          }
        }
      },
    ], 'mission-execute');

    timeoutId = setTimeout(() => {
      reject(new Error('Keep-alive mission execute request timed out'));
      console.log('💤✋❌ Keep-alive mission execute request timed out');
    }, 5000);
  });
}